import React, { useEffect, useState } from "react";
import { Form, Input, Modal, Select, Spin, theme } from "antd";
import { PRIMARY_COLOR, PRINT_COLOR, SUCCESS_COLOR } from "../../constants/color";
import { URL_PRODUCT, URL_SALE, URL_TANK } from "../../constants/urls";
import { POND_STATUS, PRODUCT_TYPE } from "../../constants/string";
import useDropdownItem from "../../common/useDropdownItem";
import { PATCH, POST } from "../../frameworks/HttpClient";
import ErrorMessages from "../../common/ErrorMessages";
import _ from "lodash";
import ColorButton from "../../common/ColorButton";

export default function StorageTankOpenLotModal(props) {
  const { open, onClose, target, onUpdate } = props;
  const [form] = Form.useForm();
  const [loading, setLoading] = React.useState(false);
  const [errorMessages, setErrorMessages] = React.useState(null);
  const [lotNoDisabled, setLotNoDisabled] = useState(false);
  const [debouncedOrderRef, setDebouncedOrderRef] = React.useState('');
  const [debouncedProduct, setDebouncedProduct] = React.useState('');
  const { token: { colorWarning, colorSuccess }, } = theme.useToken();
  const isCreate = target ? target.state !== POND_STATUS.FILLING : false

  const productDropdown = useDropdownItem({
    url: URL_PRODUCT.PRODUCT,
    label_renderer : (e) => e.short_name ? `${e.name} - ${e.short_name}` : `${e.name}`, 
    pagination: true,
    params: { page_size: 999, type: PRODUCT_TYPE.CONCENTRATE_LATEX, name: debouncedProduct },
    manual: true
  });

  const debounceProduct = React.useCallback(_.debounce((_searchVal) => { setDebouncedProduct(_searchVal) }, 800), []);

  const produceOrderDropdown = useDropdownItem({
    url: URL_SALE.PRODUCE_ORDER,
    pagination: true,
    params: { page_size: 999, type: PRODUCT_TYPE.CONCENTRATE_LATEX, lot_no : debouncedOrderRef, is_produced : false },
    label_renderer : (option) => `ล็อตที่: ${option.lot_no} ชนิดสินค้า: ${option.product_name}`,
    manual: true
  });
  const debounceOrderRef = React.useCallback(_.debounce((_searchVal) => { setDebouncedOrderRef(_searchVal) }, 800), []);

  const handleOK = async () => {
    setLoading(true)
    setErrorMessages(null);
    try {
      const data = await form.validateFields();
      if(data.order_ref){
          await delete data["product"]
          await delete data["lot_no"]
        if(data.order_ref.value)
          data["order_ref"] = data.order_ref.value;  
      } else {
        await delete data["order_ref"]
      }
      if(isCreate){
        data["tank"] = target.id;
        await POST(URL_TANK.TANK_TRANSACTION, data);
      } else {
        await PATCH(`${URL_TANK.TANK_TRANSACTION}${target.current_transaction}/`, data);
      }
      onUpdate();
    } catch (error) {
      setErrorMessages(error.errorMessages);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) { 
      setErrorMessages(null);
      productDropdown.fetchOptions();
      produceOrderDropdown.fetchOptions();
      if(isCreate){
        form.resetFields(); 
        setLotNoDisabled(false);
      } else if (target.order_ref) {
        form.setFieldsValue({...target, order_ref : {value : target.order_ref , label : `ล็อตที่:${target.lot_no} ชนิดสินค้า:${target.product_name}` }});
        setLotNoDisabled(true);
      } else {
        form.setFieldsValue({...target})
        // productDropdown.addOption({name : target.product_name, id: target.product, value :target.product});
        setLotNoDisabled(false);
      }
    }
  }, [open]);


  React.useEffect(() => {
    if(open)
      produceOrderDropdown.fetchOptions();
  },[debouncedOrderRef])

  React.useEffect(() => {
    if(open)
      productDropdown.fetchOptions();
},[debouncedProduct])

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title={isCreate ? `เพิ่มล็อต - แทงก์ ${_.get(target, "name", "-")}` : `แก้ไขล็อต - แทงก์ ${_.get(target, "name", "-")}`}
      onOk={() => handleOK()}
      width={500}
      footer={[
        <>
          {_.get(target, 'order_ref', null) && <ColorButton style={{ float : "left"}} disabled key="print" type="primary" onClick={() => handleOK()} override={PRINT_COLOR}>
            พิมพ์ใบแจ้งผลิต
          </ColorButton>}
          <ColorButton key="cancel" onClick={() => onClose()}>
            ปิด
          </ColorButton>
          {!_.get(target, 'order_ref', null) && 
            <ColorButton key="ok" type="primary" onClick={() => handleOK()} loading={loading} override={isCreate ? colorSuccess : colorWarning}>
              {isCreate ? "ตกลง" : "แก้ไข"}
            </ColorButton>
          }
        </>
      ]}
    >
      <ErrorMessages
        message={errorMessages}
        style={{ marginTop: 0, marginBottom: 10 }}
      />
      <Form
        form={form}
        labelCol={{ span: 6 }}
        wrapperCol={{ span: 18 }}
        labelAlign={"left"}
        autoComplete="off"
      >
        <Form.Item
          name={"lot_no"}
          label={"Lot No."}
          rules={[{ required: !lotNoDisabled, message: "กรุณากรอก Lot No." }]}
          >
          <Input placeholder="Lot No." disabled={lotNoDisabled} />
        </Form.Item>
        <Form.Item name={"product"} label={"เกรด"} rules={[{ required: !lotNoDisabled, message: "กรุณากรอกสินค้า" }]}>
          <Select
            showSearch
            placeholder={"เกรด"}
            options={productDropdown.options}
            disabled={lotNoDisabled}
            onSearch={debounceProduct}
            filterOption={false}
          />
        </Form.Item>
        <Form.Item
          name={"order_ref"}
          label={"ใบแจ้งผลิต"}
        >
          <Select
            showSearch
            disabled={_.get(target, 'order_ref', false) ? true : false}
            allowClear={_.get(target, 'order_ref', null) ? false : true}
            placeholder={"ใบแจ้งผลิต"}
            labelInValue={false}
            options={!isCreate ? (_.get(target, 'order_ref', null) ? [
                ...(produceOrderDropdown.options || []),
                {
                  label: `ล็อตที่: ${_.get(target, 'lot_no', null)} ชนิดสินค้า: ${_.get(target, 'product_name', null)}`,
                  value: _.get(target, 'order_ref', null),
                }
              ] : produceOrderDropdown.options) :  produceOrderDropdown.options}
            filterOption={false}
            onSearch={debounceOrderRef}
            notFoundContent={produceOrderDropdown.loading ? <Spin size="small" /> : "-"}
            onSelect={(value, option) => {
              if(value === target.order_ref){
                setLotNoDisabled(!!value);
                form.setFieldValue('lot_no', _.get(target, 'lot_no', null))
                form.setFieldValue('product', _.get(target, 'product', null))
              } else {
                setLotNoDisabled(!!value);
                form.setFieldValue('lot_no', _.get(option, 'lot_no', null))
                form.setFieldValue('product', _.get(option, 'product', null))
              }
            }}
            onClear={(value, option) => {
              setLotNoDisabled(false);
              form.setFieldValue('lot_no', null)
              form.setFieldValue('product', null)
            }}
          /> 
        </Form.Item>
      </Form>
    </Modal>
  );
}
